import { useMemo } from 'react';
import styled from 'styled-components';
import { Flex, FlexColumn, Tabs, Tab } from '@elementary/common-ui/components';
import useRouteMatch from '@elementary/common-ui/hooks/useRouteMatch';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import {
  AccountPermission,
  EnvironmentPermission,
} from '@elementary/common-ui/types';
import { Typography } from '@mui/material';
import { NavLink, Outlet } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from 'app/components/ErrorPage';
import useAccount from '@elementary/common-ui/hooks/useAccount';

const Wrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 32px 24px;
  gap: 48px;
`;

const ButtonsContainer = styled(FlexColumn)`
  justify-content: flex-start;
  gap: 16px;
  width: 208px;
  min-width: 208px;
  flex-shrink: 0;
`;

const OutletWrapper = styled.div`
  width: 1000px;
  min-width: 0;
  height: 100%;
`;

type SettingsTab = {
  label: string;
  pathname: string;
  key: string;
  accountPermissions?: AccountPermission[];
  envPermissions?: EnvironmentPermission[];
};

const userTabs: SettingsTab[] = [
  {
    label: 'My profile',
    pathname: '/settings/profile',
    key: 'profile',
  },
  {
    label: 'Personal tokens',
    pathname: '/settings/user-tokens',
    key: 'tokens',
  },
  {
    label: 'GPG keys',
    pathname: '/settings/gpg',
    key: 'gpg',
  },
];

const Settings = () => {
  const hasAccountPermission = useAccountPermission();
  const hasEnvPermission = useEnvPermission();
  const { data: account } = useAccount();

  const hasTabPermission = (tab: SettingsTab) => {
    if (tab.accountPermissions) {
      return tab.accountPermissions.some((permission) =>
        hasAccountPermission(permission),
      );
    }
    if (tab.envPermissions) {
      return tab.envPermissions.some((permission) =>
        hasEnvPermission(permission),
      );
    }
    return true;
  };

  const accountTabs = useMemo(() => {
    const tabs: SettingsTab[] = [
      {
        label: 'Users',
        pathname: '/settings/users',
        key: 'users',
        accountPermissions: ['VIEW_USERS'],
      },
    ];

    if (account?.roles_and_permissions_enabled) {
      tabs.push({
        label: 'Roles',
        pathname: '/settings/roles',
        key: 'roles',
        accountPermissions: ['VIEW_ROLES'],
      });
    }

    if (account?.has_scim_configuration) {
      tabs.push({
        label: 'Groups',
        pathname: '/settings/groups',
        key: 'groups',
        accountPermissions: ['VIEW_GROUPS'],
      });
    }

    tabs.push(
      {
        label: 'Account tokens',
        pathname: '/settings/account-tokens',
        key: 'account-tokens',
        accountPermissions: ['EDIT_USERS'],
      },
      {
        label: 'Settings',
        pathname: '/settings/account',
        key: 'account',
        accountPermissions: ['SET_TIMEZONE'],
      },
    );

    if (account?.runtimes_enabled) {
      tabs.push({
        label: 'Runtimes',
        pathname: '/settings/runtimes',
        key: 'runtimes',
        accountPermissions: ['VIEW_RUNTIMES'],
      });
    }

    tabs.push(
      {
        label: 'Logs',
        pathname: '/settings/logs',
        key: 'logs',
        accountPermissions: ['VIEW_AUDIT_LOGS', 'VIEW_SYSTEM_LOGS'],
      },
      {
        label: 'Agent Settings',
        pathname: '/settings/ai',
        key: 'ai',
      },
    );

    return tabs;
  }, [
    account?.roles_and_permissions_enabled,
    account?.has_scim_configuration,
    account?.runtimes_enabled,
  ]);

  const allTabs = [...userTabs, ...accountTabs];
  const sortedTabPaths = allTabs
    .map((t) => t.pathname)
    .sort((a, b) => b.length - a.length);
  const currentTab = useRouteMatch(sortedTabPaths);

  const relevantAccountTabs = accountTabs.filter(hasTabPermission);

  const showAccountSection = relevantAccountTabs.length > 0;

  return (
    <Wrapper>
      {currentTab && (
        <ButtonsContainer>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '12px',
              color: 'common.gray11',
            }}
          >
            USER
          </Typography>
          <Tabs
            value={
              userTabs.find((t) => t.pathname === currentTab)
                ? currentTab
                : false
            }
            orientation="vertical"
            indicatorBorder
          >
            {userTabs
              .filter(hasTabPermission)
              .map(({ key, pathname, label }) => (
                <Tab
                  sx={{
                    alignItems: 'flex-start',
                    marginLeft: '16px',
                  }}
                  key={key}
                  label={label}
                  value={pathname}
                  to={pathname}
                  component={NavLink}
                />
              ))}
          </Tabs>
          {showAccountSection && (
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '12px',
                color: 'common.gray11',
              }}
            >
              ACCOUNT
            </Typography>
          )}
          <Tabs
            value={
              accountTabs.find((t) => t.pathname === currentTab)
                ? currentTab
                : false
            }
            orientation="vertical"
            indicatorBorder
          >
            {relevantAccountTabs.map(({ key, pathname, label }) => (
              <Tab
                sx={{
                  alignItems: 'flex-start',
                  marginLeft: '16px',
                }}
                key={key}
                label={label}
                value={pathname}
                to={pathname}
                component={NavLink}
              />
            ))}
          </Tabs>
        </ButtonsContainer>
      )}

      <OutletWrapper>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <ErrorPage error={error} page={'Settings'} />
          )}
        >
          <Outlet />
        </ErrorBoundary>
      </OutletWrapper>
    </Wrapper>
  );
};

export default Settings;
